import { ImageUtils } from './../../../helper/image-utils';
import { Component, ElementRef, Input, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-image-input',
    templateUrl: './image-input.component.html',
    styleUrl: './image-input.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ImageInputComponent),
            multi: true,
        },
    ],
})
export class ImageInputComponent implements ControlValueAccessor {
    @Input() label = '';
    @Input() accept = 'image/*';

    @ViewChild('fileInput') private fileInput!: ElementRef<HTMLInputElement>;

    private _value = '';
    private _disabled = false;
    dragOver = false;

    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    get value(): string {
        return this._value;
    }
    get hasValue(): boolean {
        return !!this._value;
    }
    get previewSrc(): string {
        return this._value ? ImageUtils.convertValueToImg(this._value) : '';
    }
    get disabled(): boolean {
        return this._disabled;
    }

    public writeValue(value: string | null): void {
        this._value = value ?? '';
    }
    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
    public setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled;
    }

    public eventChooseFile(): void {
        if (this._disabled) {
            return;
        }
        this.fileInput?.nativeElement?.click();
    }
    public eventFileSelected(event: Event): void {
        if (this._disabled) {
            return;
        }
        const input = event.target as HTMLInputElement;
        const file = input.files?.item(0);
        input.value = '';

        if (file) {
            this.handleFile(file);
        }
    }
    public eventDragOver(event: DragEvent): void {
        if (this._disabled) {
            return;
        }
        event.preventDefault();
        this.dragOver = true;
    }
    public eventDragLeave(event: DragEvent): void {
        if (this._disabled) {
            return;
        }
        event.preventDefault();
        this.dragOver = false;
    }
    public eventDrop(event: DragEvent): void {
        if (this._disabled) {
            return;
        }
        event.preventDefault();
        this.dragOver = false;

        const file = event.dataTransfer?.files?.item(0);
        if (file) {
            this.handleFile(file);
        }
    }
    private handleFile(file: File): void {
        if (this.isSvg(file)) {
            this.readAsSvg(file);
        } else {
            this.readAsBase64(file);
        }
    }

    private isSvg(file: File): boolean {
        return file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    }
    private readAsSvg(file: File): void {
        const reader = new FileReader();
        reader.onload = () => {
            this._value = String(reader.result ?? '');
            this.emitValue();
        };
        reader.readAsText(file);
    }
    private readAsBase64(file: File): void {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result ?? '');
            this._value = result.includes(',') ? result.split(',')[1] : result;
            this.emitValue();
        };
        reader.readAsDataURL(file);
    }
    private emitValue(): void {
        this.onChange(this._value);
        this.onTouched();
    }
}
